const ADMIN_TOKEN_STORAGE_KEY = "panetteria_admin_api_token";
const ADMIN_AUTH_MODE = (process.env.REACT_APP_ADMIN_AUTH_MODE || "token").trim().toLowerCase();

export const STATUS_OPTIONS = ["pending_payment", "paid", "shipped", "cancelled"];
export const STATUS_LABELS = {
  pending_payment: "In attesa pagamento",
  paid: "Pagato",
  shipped: "Spedito",
  cancelled: "Annullato"
};

export const EMPTY_NEW_PRODUCT = {
  name: "",
  slug: "",
  description: "",
  price_cents: "",
  weight_grams: "",
  stock_qty: "",
  image_url: "",
  active: true
};

export const MENU_LOCATION_OPTIONS = [
  { value: "HEADER", label: "Header" },
  { value: "FOOTER", label: "Footer" }
];

export const EMPTY_NEW_MENU_ITEM = {
  location: "HEADER",
  label: "",
  url: "",
  description: "",
  sort_order: "100",
  active: true
};

export function readStoredToken() {
  if (typeof window === "undefined") {
    return "";
  }
  return window.localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY) || "";
}

export function persistToken(token) {
  if (typeof window === "undefined") {
    return;
  }
  if (!token) {
    window.localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, token);
}

export function isCognitoAdminAuthMode() {
  return ADMIN_AUTH_MODE === "cognito";
}

export function buildAdminAuthHeaders(adminToken) {
  if (!adminToken) {
    return {};
  }

  if (isCognitoAdminAuthMode()) {
    return {
      Authorization: `Bearer ${adminToken}`
    };
  }

  return {
    "x-admin-token": adminToken
  };
}

export function isUnauthorizedAdminError(error) {
  if (!error) {
    return false;
  }

  if (typeof error === "object" && Number(error.status) === 401) {
    return true;
  }

  const message = error instanceof Error ? error.message : String(error);
  return message.toLowerCase().includes("unauthorized");
}

export function formatDateTime(value) {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return new Intl.DateTimeFormat("it-IT", {
    dateStyle: "short",
    timeStyle: "short"
  }).format(date);
}

export function shortOrderId(orderId) {
  if (!orderId || typeof orderId !== "string") {
    return "-";
  }
  return orderId.length <= 12 ? orderId : `${orderId.slice(0, 8)}...${orderId.slice(-4)}`;
}

export function shortProductId(productId) {
  if (!productId || typeof productId !== "string") {
    return "-";
  }
  return productId.length <= 18 ? productId : `${productId.slice(0, 12)}...${productId.slice(-4)}`;
}

export function countOrderItems(items) {
  if (!Array.isArray(items)) {
    return 0;
  }
  return items.reduce((total, item) => total + (Number(item?.qty) || 0), 0);
}

export function getCityFromOrder(order) {
  const shippingAddress = order?.shipping_address_json?.address;
  const billingAddress = order?.address_json?.address;
  return shippingAddress?.city || billingAddress?.city || "-";
}

export function toProductDraft(product) {
  return {
    name: product?.name || "",
    slug: product?.slug || "",
    description: product?.description || "",
    price_cents: String(product?.price_cents ?? ""),
    weight_grams: String(product?.weight_grams ?? ""),
    stock_qty: String(product?.stock_qty ?? ""),
    image_url: product?.image_url || "",
    active: Boolean(product?.active)
  };
}

export function toProductPayload(draft) {
  return {
    name: draft.name,
    slug: draft.slug,
    description: draft.description,
    price_cents: Number(draft.price_cents),
    weight_grams: Number(draft.weight_grams),
    stock_qty: Number(draft.stock_qty),
    image_url: draft.image_url,
    active: Boolean(draft.active)
  };
}

export function shortMenuItemId(itemId) {
  if (!itemId || typeof itemId !== "string") {
    return "-";
  }
  return itemId.length <= 18 ? itemId : `${itemId.slice(0, 12)}...${itemId.slice(-4)}`;
}

export function toMenuItemDraft(item) {
  return {
    location: item?.location || "HEADER",
    label: item?.label || "",
    url: item?.url || "",
    description: item?.description || "",
    sort_order: String(item?.sort_order ?? 100),
    active: Boolean(item?.active)
  };
}

export function toMenuItemPayload(draft) {
  const url = typeof draft.url === "string" ? draft.url.trim() : "";
  const description = typeof draft.description === "string" ? draft.description.trim() : "";

  return {
    location: draft.location,
    label: draft.label,
    url: url || undefined,
    description: description || undefined,
    sort_order: Number(draft.sort_order),
    active: Boolean(draft.active)
  };
}
